import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Flyout } from "@/shared/components/ui/flyout";
import { Plus, Search } from "lucide-react";
import { Integration, IntegrationBase } from "../types/integration";
import { IntegrationService } from "../services/integrationsService";
import { IntegrationBaseService } from "../services/integrationsBaseService";
import { getIntegrationDescription } from '@/shared/utils/i18nHelper';
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";

interface IntegrationsSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: Integration) => void;
}

const IntegrationsSelector = ({ 
  isOpen, 
  onClose, 
  onSelectTemplate 
}: IntegrationsSelectorProps) => {
  const { t, i18n } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [installedBases, setInstalledBases] = useState<IntegrationBase[]>([]);

  // SEGURANÇA: Limpar campo de pesquisa sempre que o modal abrir
  useEffect(() => {
    if (isOpen) {
      setTemplateSearchQuery("");
      // Carregar integrações instaladas para mostrar status
      if (currentWorkspace?.organization_id) {
        IntegrationBaseService.getAll({
          status: 'active',
          ...(currentWorkspace?.id ? { product_id: currentWorkspace.id } : {})
        }).then(setInstalledBases).catch(() => setInstalledBases([]));
      }
    }
  }, [isOpen, currentWorkspace?.organization_id, currentWorkspace?.id]);

  // Buscar templates disponíveis
  const { 
    data: templates, 
    isLoading: isLoadingTemplates 
  } = useQuery({
    queryKey: ["integration-templates"],
    queryFn: () => IntegrationService.getAll(),
  });

  // Filtrar e ordenar templates baseado na pesquisa
  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    
    // Filtrar templates bloqueadas (block: true) - não devem aparecer para seleção
    let filtered = templates.filter(template => template.settings?.block !== true);
    
    if (templateSearchQuery.trim()) {
      const query = templateSearchQuery.toLowerCase();
      filtered = filtered.filter(template => {
        const description = getIntegrationDescription(template, i18n.language);
        return template.label.toLowerCase().includes(query) ||
          description?.toLowerCase().includes(query);
      });
    }
    
    // Ordenar: ativas primeiro, depois inativas
    return filtered.sort((a, b) => {
      const aActive = a.active !== false;
      const bActive = b.active !== false;
      
      if (aActive === bActive) return 0;
      return aActive ? -1 : 1;
    });
  }, [templates, templateSearchQuery, i18n.language]);

  const handleSelectTemplate = (template: Integration) => {
    onSelectTemplate(template);
    handleClose();
  };

  const handleClose = () => {
    // SEGURANÇA: Sempre limpar campo de pesquisa ao fechar
    setTemplateSearchQuery("");
    onClose();
  };

  return (
    <Flyout
      open={isOpen}
      onClose={handleClose}
    >
      <div className="space-y-6">
        {/* Header do flyout */}
        <div className="pt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-full bg-primary/10">
              <Plus className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold">{t("integrations.addIntegration")}</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("integrations.selectTemplate")}
          </p>
        </div>
        
        {/* Search Bar no flyout */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t("integrations.searchTools")}
            value={templateSearchQuery}
            onChange={(e) => setTemplateSearchQuery(e.target.value)}
            className="pl-10 h-10 bg-background"
          />
        </div>
        
        {/* Lista de templates */}
        <div className="space-y-3">
          {isLoadingTemplates ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
            </div>
          ) : filteredTemplates && filteredTemplates.length > 0 ? (
            filteredTemplates.map((template) => {
              const isInactive = template.active === false;

              // Desabilitar se já instalado como workspace-specific neste produto
              const isDisabledByInstall = installedBases.some(
                (ib) => ib.name === template.name && ib.scope === 'local'
              );

              return (
                <Card
                  key={template.id}
                  className={`transition-shadow ${
                    isInactive || isDisabledByInstall
                      ? 'opacity-40 grayscale cursor-not-allowed'
                      : 'hover:shadow-md cursor-pointer hover:border-primary/50'
                  }`}
                  onClick={() => !isInactive && !isDisabledByInstall && handleSelectTemplate(template)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                        <img
                          src={template.logo_url || ''}
                          alt={template.label}
                          className="aspect-square h-full w-full object-contain p-1.5"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm">{template.label}</h3>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {getIntegrationDescription(template, i18n.language) || t('integrations.empty.description')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          disabled={isInactive || isDisabledByInstall}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {t('integrations.selector.noToolsFound')}
            </div>
          )}
        </div>
      </div>
    </Flyout>
  );
};

export default IntegrationsSelector;
